<script setup>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import API from 'dashboard/api/wabaTemplates';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import TemplatePageHeader from './templates/TemplatePageHeader.vue';
import ChannelPhonePreview from 'dashboard/components-next/message/preview/ChannelPhonePreview.vue';
import { messages, statusClass } from './templates/wabaEditor';

const { t, te } = useI18n({ useScope: 'local', messages });
const rows = ref([]);
const sources = ref([]);
const busy = ref(false);
const loading = ref(true);
const error = ref('');
const search = ref('');
const status = ref('');
const filtersOpen = ref(false);
const sourceId = ref('');
const editing = ref(false);
const record = ref(null);
const form = reactive({
  title: '',
  inbox_id: '',
  category: 'UTILITY',
  language: 'ru',
  body: '',
  footer: '',
  header: '',
  examples: {},
  buttons: [],
});
const saved = ref('');
const snapshot = () => JSON.stringify(form);
const locked = computed(
  () =>
    !!record.value &&
    (record.value.status !== 'draft' || !!record.value.provider_id)
);
const dirty = computed(
  () => editing.value && !locked.value && snapshot() !== saved.value
);
const selectedSource = computed(() =>
  sources.value.find(s => s.id === Number(form.inbox_id))
);
const variables = computed(() =>
  [
    ...new Set(
      [...form.body.matchAll(/\{\{(\d+)\}\}/g)].map(m => Number(m[1]))
    ),
  ].sort((a, b) => a - b)
);
const filtered = computed(() =>
  rows.value.filter(
    r =>
      (!status.value || r.status === status.value) &&
      `${r.title} ${r.binding.body}`
        .toLowerCase()
        .includes(search.value.toLowerCase())
  )
);
const statuses = computed(() => [...new Set(rows.value.map(r => r.status))]);
const statusLabel = value =>
  te(`statuses.${value}`) ? t(`statuses.${value}`) : value;
const canCopy = computed(
  () =>
    !record.value ||
    (record.value.components.every(
      c =>
        ['BODY', 'FOOTER', 'BUTTONS'].includes(c.type) ||
        (c.type === 'HEADER' && c.format === 'TEXT')
    ) &&
      record.value.components
        .filter(c => c.type === 'BUTTONS')
        .every(c =>
          c.buttons.every(b => ['URL', 'QUICK_REPLY'].includes(b.type))
        ))
);
const preview = computed(() => ({
  text: [
    form.header,
    form.body.replace(
      /\{\{(\d+)\}\}/g,
      (match, n) => form.examples[n] || match
    ),
    form.footer,
  ]
    .filter(Boolean)
    .join('\n\n'),
  buttons: form.buttons.map(b => ({
    type: b.type === 'QUICK_REPLY' ? 'quick_reply' : 'url',
    text: b.text,
    url: b.url,
  })),
}));
const describeError = failure => {
  const data = failure.response?.data || {};
  return te(`errors.${data.error}`)
    ? t(`errors.${data.error}`)
    : data.error || data.message || t('error');
};
const allowLeave = () => {
  // eslint-disable-next-line no-alert
  return !dirty.value || window.confirm(t('discard'));
};
onBeforeRouteLeave(allowLeave);
const beforeUnload = event => {
  if (dirty.value) {
    event.preventDefault();
    event.returnValue = '';
  }
};
onUnmounted(() => window.removeEventListener('beforeunload', beforeUnload));
const load = async () => {
  loading.value = true;
  try {
    const { data } = await API.get();
    rows.value = data.templates;
    sources.value = data.sources;
    if (!sourceId.value) sourceId.value = sources.value[0]?.id || '';
  } catch (failure) {
    error.value = describeError(failure);
  } finally {
    loading.value = false;
  }
};
onMounted(() => {
  load();
  window.addEventListener('beforeunload', beforeUnload);
});
const open = (item = null, copy = false) => {
  if (!allowLeave()) return;
  const components = item?.components || [];
  const body = components.find(c => c.type === 'BODY');
  const examples = body?.example;
  const values = Array.isArray(examples)
    ? examples
    : examples?.body_text?.[0] || [];
  Object.assign(form, {
    title: item ? `${item.title}${copy ? ' — 2' : ''}` : '',
    inbox_id: item?.inbox_id || sourceId.value,
    category: item?.category || 'UTILITY',
    language: item?.language || 'ru',
    body: body?.text || '',
    header: components.find(c => c.type === 'HEADER')?.text || '',
    footer: components.find(c => c.type === 'FOOTER')?.text || '',
    examples: Object.fromEntries(values.map((v, i) => [i + 1, v])),
    buttons: JSON.parse(
      JSON.stringify(components.find(c => c.type === 'BUTTONS')?.buttons || [])
    ),
  });
  record.value = copy ? null : item;
  saved.value = snapshot();
  error.value = '';
  editing.value = true;
};
const back = () => {
  if (allowLeave()) {
    editing.value = false;
    error.value = '';
  }
};
const payload = () => ({
  title: form.title.trim(),
  inbox_id: Number(form.inbox_id),
  category: form.category,
  language: form.language,
  components: [
    ...(form.header
      ? [{ type: 'HEADER', format: 'TEXT', text: form.header }]
      : []),
    {
      type: 'BODY',
      text: form.body,
      example: variables.value.map(n => form.examples[n] || ''),
    },
    ...(form.footer ? [{ type: 'FOOTER', text: form.footer }] : []),
    ...(form.buttons.length
      ? [
          {
            type: 'BUTTONS',
            buttons: form.buttons.map(b => ({
              type: b.type,
              text: b.text,
              ...(b.type === 'URL' ? { url: b.url } : {}),
            })),
          },
        ]
      : []),
  ],
});
const save = async () => {
  if (busy.value) return;
  if (!form.title.trim() || !form.inbox_id || !form.body.trim()) {
    error.value = t('required');
    return;
  }
  busy.value = true;
  error.value = '';
  try {
    const { data } = record.value
      ? await API.update(record.value.id, payload())
      : await API.create(payload());
    record.value = data;
    saved.value = snapshot();
    await load();
    useAlert(t('saved'));
  } catch (failure) {
    error.value = describeError(failure);
  } finally {
    busy.value = false;
  }
};
const sync = async () => {
  if (!sourceId.value || busy.value) return;
  busy.value = true;
  error.value = '';
  try {
    await API.sync(Number(sourceId.value));
    await load();
    useAlert(t('synced'));
  } catch (failure) {
    error.value = describeError(failure);
  } finally {
    busy.value = false;
  }
};
const submit = async () => {
  if (
    busy.value ||
    dirty.value ||
    !record.value ||
    !selectedSource.value?.can_submit
  )
    return;
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('confirm', { name: form.title }))) return;
  busy.value = true;
  error.value = '';
  try {
    const { data } = await API.submit(record.value.id);
    record.value = data;
    await load();
    useAlert(t('sent'));
  } catch (failure) {
    error.value = describeError(failure);
    await load();
    record.value =
      rows.value.find(r => r.id === record.value.id) || record.value;
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <div
    class="flex flex-col w-full h-full min-h-0 overflow-hidden bg-n-surface-1 text-n-slate-12"
  >
    <TemplatePageHeader
      v-model:search="search"
      :title="t('title')"
      :show-search="!editing"
      :search-placeholder="t('search')"
    >
      <template v-if="!editing">
        <div class="relative">
          <Button
            icon="i-lucide-list-filter"
            :aria-label="t('filters')"
            :title="t('filters')"
            size="sm"
            color="slate"
            variant="ghost"
            :class="status || filtersOpen ? 'bg-n-alpha-2' : ''"
            :aria-expanded="filtersOpen"
            @click="filtersOpen = !filtersOpen"
          />
          <div
            v-if="filtersOpen"
            v-on-clickaway="() => (filtersOpen = false)"
            class="absolute top-full right-0 mt-1 flex flex-col gap-4 bg-n-alpha-3 backdrop-blur-[100px] border border-n-weak w-72 rounded-xl p-4 z-50"
            @keydown.esc="filtersOpen = false"
          >
            <div class="flex items-center justify-between gap-2">
              <span class="text-sm text-n-slate-12">{{ t('status') }}</span>
              <SelectMenu
                v-model="status"
                sub-menu-position="left"
                :options="[
                  { value: '', label: t('all') },
                  ...statuses.map(s => ({ value: s, label: statusLabel(s) })),
                ]"
                :label="status ? statusLabel(status) : t('all')"
              />
            </div>
            <div class="flex items-center justify-between gap-2">
              <span class="text-sm text-n-slate-12">{{ t('source') }}</span>
              <SelectMenu
                :model-value="String(sourceId)"
                sub-menu-position="left"
                :options="
                  sources.map(s => ({ value: String(s.id), label: s.name }))
                "
                :label="
                  sources.find(s => s.id === Number(sourceId))?.name ||
                  t('choose')
                "
                @update:model-value="sourceId = Number($event)"
              />
            </div>
          </div>
        </div>
        <Button
          icon="i-lucide-refresh-cw"
          :aria-label="t('sync')"
          :title="t('sync')"
          size="sm"
          color="slate"
          variant="ghost"
          :is-loading="busy"
          :disabled="busy || !sourceId"
          @click="sync"
        />
        <Button
          v-if="sources.length && !sources.some(s => s.can_submit)"
          icon="i-lucide-info"
          :aria-label="t('auth')"
          :title="t('auth')"
          size="sm"
          color="slate"
          variant="ghost"
        />
        <div class="w-px h-4 bg-n-strong" />
        <Button
          :label="t('create')"
          size="sm"
          icon="i-lucide-plus"
          :disabled="!sources.length || busy"
          @click="open()"
        />
      </template>
      <template v-else>
        <Button
          :label="t('back')"
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-arrow-left"
          :disabled="busy"
          @click="back"
        />
        <Button
          v-if="!locked"
          :label="t('save')"
          size="sm"
          :disabled="busy"
          @click="save"
        />
        <Button
          v-if="!locked"
          :label="t('submit')"
          variant="outline"
          size="sm"
          :disabled="busy || dirty || !record || !selectedSource?.can_submit"
          @click="submit"
        />
        <Button
          v-else
          :label="t('copy')"
          size="sm"
          :disabled="!canCopy || busy"
          @click="open(record, true)"
        />
      </template>
    </TemplatePageHeader>
    <p
      v-if="error"
      role="alert"
      class="mx-6 mt-4 p-3 rounded-lg bg-n-ruby-3 text-n-ruby-11 whitespace-pre-wrap"
    >
      {{ error }}
    </p>
    <main v-if="!editing" class="flex-1 min-h-0 overflow-auto px-6 pb-6">
      <div v-if="status" class="flex items-center gap-2 mb-4">
        <Button
          :label="statusLabel(status)"
          icon="i-lucide-x"
          trailing-icon
          size="sm"
          color="slate"
          variant="faded"
          @click="status = ''"
        />
      </div>
      <p v-if="!loading && !sources.length" class="text-sm text-n-slate-11">
        {{ t('noSource') }}
      </p>
      <p
        v-if="loading || !filtered.length"
        class="py-10 text-center text-sm text-n-slate-11"
      >
        {{ loading ? t('loading') : rows.length ? t('noResults') : t('empty') }}
      </p>
      <div
        v-else
        class="overflow-x-auto border rounded-xl border-n-weak bg-n-solid-1"
      >
        <table class="w-full text-sm text-left border-collapse">
          <thead class="text-n-slate-11">
            <tr class="border-b border-n-weak">
              <th class="px-3 py-2.5 font-medium whitespace-nowrap">
                {{ t('name') }}
              </th>
              <th class="px-3 py-2.5 font-medium whitespace-nowrap">
                {{ t('category') }}
              </th>
              <th class="px-3 py-2.5 font-medium whitespace-nowrap">
                {{ t('status') }}
              </th>
              <th class="px-3 py-2.5 font-medium whitespace-nowrap">
                {{ t('updated') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in filtered"
              :key="item.id"
              class="border-b border-n-weak last:border-0 hover:bg-n-alpha-1 cursor-pointer"
              @click="open(item)"
            >
              <td class="px-3 py-2.5">
                <button
                  class="font-medium text-left text-n-slate-12 hover:underline"
                  @click.stop="open(item)"
                >
                  {{ item.title }}
                </button>
                <p class="mt-1 mb-0 max-w-md truncate text-xs text-n-slate-11">
                  {{ item.binding.body }}
                </p>
              </td>
              <td class="px-3 py-2.5 text-n-slate-11">
                {{
                  item.category === 'UTILITY'
                    ? t('utility')
                    : item.category === 'MARKETING'
                      ? t('marketing')
                      : item.category
                }}<span class="block mt-1 text-xs text-n-slate-10"
                  >{{ item.language }} · {{ item.waba.wabaName }}</span
                >
              </td>
              <td class="px-3 py-2.5 whitespace-nowrap">
                <span
                  class="inline-block px-2 py-0.5 rounded-md text-xs"
                  :class="statusClass(item.status)"
                  >{{ statusLabel(item.status) }}</span
                >
              </td>
              <td
                class="px-3 py-2.5 text-n-slate-11 whitespace-nowrap tabular-nums"
              >
                {{
                  item.synced_at
                    ? new Date(item.synced_at).toLocaleString()
                    : '—'
                }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </main>
    <main
      v-else
      class="flex-1 min-h-0 overflow-y-auto grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-8 px-6 pb-6"
    >
      <div class="max-w-3xl space-y-5">
        <div v-if="record" class="flex items-center gap-3">
          <span
            class="px-2 py-1 rounded-md text-xs"
            :class="statusClass(record.status)"
            >{{ statusLabel(record.status) }}</span
          ><span v-if="record.provider_id" class="text-xs text-n-slate-10">{{
            record.provider_id
          }}</span>
        </div>
        <p v-if="locked" class="text-sm text-n-slate-11">{{ t('readonly') }}</p>
        <p
          v-if="!selectedSource?.can_submit && !locked"
          class="p-3 rounded-lg bg-n-amber-3 text-sm text-n-amber-11"
        >
          {{ t('auth') }}
        </p>
        <p
          v-if="record?.rejected_reason && record.rejected_reason !== 'NONE'"
          class="text-sm text-n-ruby-11"
        >
          {{ t('reason') }}: {{ record.rejected_reason }}
        </p>
        <p
          v-if="record?.provider_id && !record.available"
          class="text-sm text-n-amber-11"
        >
          {{ t('unavailable') }}
        </p>
        <p v-if="record && !record.supported" class="text-sm text-n-amber-11">
          {{ t('unsupported') }}
        </p>
        <fieldset :disabled="locked || busy" class="space-y-5">
          <Input
            v-model="form.title"
            :label="t('name')"
            :disabled="locked || busy"
          />
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label class="block text-sm"
              >{{ t('source')
              }}<select v-model="form.inbox_id" class="w-full mt-2">
                <option value="">{{ t('choose') }}</option>
                <option v-for="s in sources" :key="s.id" :value="s.id">
                  {{ s.name }}
                </option>
              </select></label
            >
            <label class="block text-sm"
              >{{ t('category')
              }}<select v-model="form.category" class="w-full mt-2">
                <option value="UTILITY">{{ t('utility') }}</option>
                <option value="MARKETING">{{ t('marketing') }}</option>
              </select></label
            >
            <Input
              v-model="form.language"
              :label="t('language')"
              :disabled="locked || busy"
            />
          </div>
          <Input
            v-model="form.header"
            :label="t('header')"
            maxlength="60"
            :disabled="locked || busy"
          />
          <label class="block text-sm"
            >{{ t('body')
            }}<textarea
              v-model="form.body"
              rows="7"
              class="w-full mt-2"
              maxlength="1024"
            />
          </label>
          <p class="text-xs text-n-slate-11">
            {{ t('variableHint', { example: '{' + '{1}' + '}' }) }}
          </p>
          <Input
            v-for="n in variables"
            :key="n"
            v-model="form.examples[n]"
            :label="t('example', { number: n })"
            :disabled="locked || busy"
          />
          <Input
            v-model="form.footer"
            :label="t('footer')"
            maxlength="60"
            :disabled="locked || busy"
          />
          <div class="space-y-3">
            <h2 class="text-sm font-medium">{{ t('buttons') }}</h2>
            <div
              v-for="(button, index) in form.buttons"
              :key="index"
              class="flex flex-wrap gap-2"
            >
              <select
                v-model="button.type"
                :aria-label="t('buttons')"
                class="!w-auto"
              >
                <option value="QUICK_REPLY">{{ t('reply') }}</option>
                <option value="URL">{{ t('url') }}</option>
              </select>
              <input
                v-model="button.text"
                :aria-label="t('label')"
                :placeholder="t('label')"
                class="flex-1"
              />
              <input
                v-if="button.type === 'URL'"
                v-model="button.url"
                :aria-label="t('url')"
                :placeholder="t('url')"
                class="flex-1"
              />
              <button
                v-if="!locked"
                class="text-sm text-n-ruby-11"
                @click="form.buttons.splice(index, 1)"
              >
                {{ t('remove') }}
              </button>
            </div>
            <Button
              v-if="!locked"
              :label="t('addButton')"
              variant="ghost"
              :disabled="form.buttons.length >= 3"
              @click="form.buttons.push({ type: 'QUICK_REPLY', text: '' })"
            />
          </div>
        </fieldset>
        <pre
          v-if="record && !canCopy"
          class="p-4 overflow-auto text-xs rounded-lg bg-n-alpha-1"
          >{{ JSON.stringify(record.components, null, 2) }}</pre
        >
      </div>
      <aside class="xl:sticky xl:top-6 self-start">
        <h2 class="text-sm text-center font-medium">{{ t('preview') }}</h2>
        <ChannelPhonePreview
          channel="whatsapp"
          :preview="preview"
          :missing-message="t('body')"
        />
      </aside>
    </main>
  </div>
</template>
